<template>
  <li
    v-if="canViewVerticalNavMenuGroup(item)"
    ref="groupRef"
    class="main-menu-group"
    :class="{
      'main-menu-group--open': isOpen,
      'main-menu-group--disabled': item.disabled,
    }"
    @mouseenter="openSubmenu"
    @mouseleave="startCloseTimer"
  >
    <div class="main-menu-group__trigger">
      <span class="main-menu-group__text">{{ $tc(item.title, 2) }}</span>
      <svg
        class="main-menu-group__arrow"
        width="7"
        height="12"
        viewBox="0 0 7 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1 1L6 6L1 11"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>
    <main-menu-panel
      v-if="isOpen"
      :items="item.children"
      :anchor-el="$refs.groupRef"
      :level="level + 1"
      @close-all="$emit('close-all')"
      @navigate="$emit('navigate')"
      @mouse-enter-panel="cancelCloseTimer"
      @mouse-leave-panel="startCloseTimer"
    />
  </li>
</template>

<script>
import { ref, inject, watch, onBeforeUnmount } from '@vue/composition-api'
import { useUtils as useAclUtils } from '@core/libs/acl'

export default {
  name: 'MainMenuGroup',
  beforeCreate() {
    // Lazy registration to resolve circular dependency: MainMenuGroup <-> MainMenuPanel
    this.$options.components.MainMenuPanel = require('./MainMenuPanel.vue').default
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    level: {
      type: Number,
      default: 0,
    },
  },
  setup(props, { emit }) {
    const { canViewVerticalNavMenuGroup } = useAclUtils()
    const isOpen = ref(false)
    let closeTimer = null

    const menuControl = inject('mainMenuControl', null)

    // Coordinate with sibling groups inside the same panel so that
    // only one submenu can be open at a time (prevents overlap on hover).
    const activeGroupId = inject('panelActiveGroupId', null)
    let uid = null // lazily assigned unique id

    const openSubmenu = () => {
      cancelCloseTimer()
      isOpen.value = true

      // Tell siblings to close by marking ourselves as the active group
      if (activeGroupId) {
        if (!uid) uid = Math.random()
        activeGroupId.value = uid
      }
    }

    const startCloseTimer = () => {
      cancelCloseTimer()
      closeTimer = setTimeout(() => {
        isOpen.value = false
        closeTimer = null
      }, 500)
    }

    const cancelCloseTimer = () => {
      if (closeTimer) {
        clearTimeout(closeTimer)
        closeTimer = null
      }
    }

    // Listen for global close signal
    if (menuControl && menuControl.closeAllTick) {
      watch(
        () => menuControl.closeAllTick.value,
        () => {
          isOpen.value = false
          cancelCloseTimer()
        }
      )
    }

    // When a sibling group becomes active, close this group immediately
    if (activeGroupId) {
      watch(
        () => activeGroupId.value,
        (newId) => {
          if (uid && newId !== uid && isOpen.value) {
            cancelCloseTimer()
            isOpen.value = false
          }
        }
      )
    }

    onBeforeUnmount(() => {
      cancelCloseTimer()
    })

    return {
      isOpen,
      canViewVerticalNavMenuGroup,
      openSubmenu,
      startCloseTimer,
      cancelCloseTimer,
    }
  },
}
</script>

<style scoped>

.main-menu-group {
  list-style: none;
  position: relative;
}

.main-menu-group__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.55rem 1.25rem;
  color: #625f6e;
  cursor: pointer;
  font-size: 0.95rem;
  white-space: nowrap;
  transition: background-color 0.15s ease, color 0.15s ease;
  user-select: none;
}

.main-menu-group__trigger:hover {
  color: var(--primary);
  background-color: rgba(27, 61, 94, 0.12);
}

.main-menu-group--open > .main-menu-group__trigger {
  color: var(--primary);
  background-color: rgba(27, 61, 94, 0.12);
}

.main-menu-group--disabled {
  pointer-events: none;
  opacity: 0.5;
}

.main-menu-group__text {
  text-transform: capitalize;
  margin-right: 1rem;
}

.main-menu-group__arrow {
  flex-shrink: 0;
  color: inherit;
}

.dark-layout .main-menu-group__trigger {
  color: #d0d2d6;
}

.dark-layout .main-menu-group__trigger:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.dark-layout .main-menu-group--open > .main-menu-group__trigger {
  background-color: rgba(255, 255, 255, 0.08);
}
</style>
